import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  createStyles,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import useWorkoutBenchmarks from 'hooks/workoutBenchmark/useWorkoutBenchmarks';
import { SCORE_TYPE_OPTIONS, ScoreTypeEnum } from 'types/enums';

import { SelectedBenchmark } from '../../../../../forms/workoutBuilder/types';

export interface BenchmarkDraft {
  name: string;
  scoreType: ScoreTypeEnum | null;
}

interface IModalSaveAsBenchmarkProps {
  opened: boolean;
  /** Prefill for the benchmark name (mirrors the section name). */
  sectionName: string;
  /** Section rich-text description; becomes the benchmark description (read-only here). */
  sectionDescription: string;
  /** Current section score type, if any, prefilling the scoring select. */
  initialScoreType: ScoreTypeEnum | null;
  /** "Just score this section" — keep it a one-off (soft-deleted server side). */
  onJustScore: () => void;
  /** "Save as Benchmark" — the benchmark is created at workout-save from the section. */
  onSaveAsBenchmark: (draft: BenchmarkDraft) => void;
  /** Chosen name already exists — link that benchmark instead of creating a duplicate. */
  onLinkExisting: (benchmark: SelectedBenchmark) => void;
  /** Dismissed without choosing (X / backdrop) — caller reverts the toggle. */
  onClose: () => void;
}

const useStyles = createStyles(theme => ({
  previewCard: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
  },
  sectionLabel: {
    letterSpacing: '0.04em',
  },
}));

const ModalSaveAsBenchmark = ({
  opened,
  sectionName,
  sectionDescription,
  initialScoreType,
  onJustScore,
  onSaveAsBenchmark,
  onLinkExisting,
  onClose,
}: IModalSaveAsBenchmarkProps): JSX.Element => {
  const { classes } = useStyles();

  const [name, setName] = useState(sectionName);
  const [scoreType, setScoreType] = useState<ScoreTypeEnum | null>(initialScoreType);
  const [nameError, setNameError] = useState<string | null>(null);

  const descriptionMissing = sectionDescription.replace(/<[^>]*>/g, '').trim() === '';

  const [debouncedName] = useDebouncedValue(name.trim(), 250);
  const { data: benchmarkData } = useWorkoutBenchmarks({ search: debouncedName });
  const existingMatch = useMemo(() => {
    const query = debouncedName.toLowerCase();
    if (!query) return null;
    const rows = (benchmarkData?.pages ?? []).flatMap(page => page.data.results);
    return rows.find(b => b.name.trim().toLowerCase() === query) ?? null;
  }, [benchmarkData, debouncedName]);

  useEffect(() => {
    if (opened) {
      setName(sectionName);
      setScoreType(initialScoreType);
      setNameError(null);
    }
  }, [opened, sectionName, initialScoreType]);

  const handleSaveAsBenchmark = (): void => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Benchmark name is required');
      return;
    }
    // Nothing is persisted here — the benchmark is created at workout-save.
    onSaveAsBenchmark({ name: trimmed, scoreType });
  };

  const handleLinkExisting = (): void => {
    if (!existingMatch) return;
    onLinkExisting({
      id: existingMatch.workoutMovementId,
      name: existingMatch.name,
      defaultScoreType: (existingMatch.defaultScoreType as ScoreTypeEnum | '' | null) || null,
      description: existingMatch.description ?? null,
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size='lg'
      title={<Title order={3}>Save as a Benchmark?</Title>}
    >
      <Stack spacing='md'>
        <Text color='dimmed' size='sm'>
          A benchmark is a named, repeatable workout that gets saved to your library. When you use it again
          in future classes, athlete scores are linked — so you can track progress and compare results over
          time.
        </Text>

        <Divider />

        <Alert color='yellow' variant='light'>
          <Text size='sm'>
            <b>Example:</b> If you save &quot;Fran&quot; as a benchmark, every time a coach programs Fran and
            logs scores, athletes can see their previous times and compare.
          </Text>
        </Alert>

        <Text size='xs' weight={700} color='dimmed' className={classes.sectionLabel}>
          HOW IT WILL BE SAVED IN LIBRARY → BENCHMARKS
        </Text>

        <div className={classes.previewCard}>
          <Stack spacing='sm'>
            <TextInput
              label='Benchmark Name'
              placeholder='Enter benchmark name'
              required
              value={name}
              onChange={e => {
                setName(e.currentTarget.value);
                if (e.currentTarget.value.trim()) setNameError(null);
              }}
              error={nameError}
              data-cy='saveAsBenchmarkName'
            />
            <WorkoutRichTextEditor
              label='Description (from section)'
              value={sectionDescription}
              onChange={() => undefined}
              editable={false}
              dataCy='saveAsBenchmarkDescription'
            />
            {descriptionMissing && (
              <Text size='xs' color='dimmed'>
                Add a workout description to this section to save it as a benchmark.
              </Text>
            )}
            <Select
              label='Scoring Type'
              placeholder='Choose a scoring type…'
              data={SCORE_TYPE_OPTIONS}
              value={scoreType}
              onChange={value => setScoreType((value as ScoreTypeEnum) || null)}
              clearable
              data-cy='saveAsBenchmarkScoreType'
            />
          </Stack>
        </div>

        {existingMatch && (
          <Alert color='blue' variant='light' data-cy='benchmarkNameExists'>
            <Group position='apart' align='center' noWrap>
              <Text size='sm'>A benchmark named &quot;{existingMatch.name}&quot; already exists.</Text>
              <Button size='xs' variant='light' onClick={handleLinkExisting} data-cy='linkExistingBenchmark'>
                Link it instead
              </Button>
            </Group>
          </Alert>
        )}

        <Group position='right' mt='sm'>
          <Button variant='subtle' color='gray' onClick={onJustScore} data-cy='justScoreThisSection'>
            Just score this section
          </Button>
          <Button
            onClick={handleSaveAsBenchmark}
            disabled={descriptionMissing || !!existingMatch}
            data-cy='saveAsBenchmark'
          >
            Save as Benchmark
          </Button>
        </Group>
        <Text size='xs' color='dimmed' align='right'>
          This benchmark will be added to your library when you save the workout.
        </Text>
      </Stack>
    </Modal>
  );
};

export default ModalSaveAsBenchmark;
